import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  Checkbox,
  Alert,
  Spin,
  message,
  Typography,
  Space,
  Tag,
} from 'antd';
import { exportRequirementsToFeishu } from '@/api/requirements';

const { Text, Link } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  onSuccess?: () => void;
}

interface FieldChange {
  field_name: string;
  field_key: string;
  db_value: string;
  feishu_value: string;
}

interface ChangeDetail {
  req_id: string;
  operation_type: string;
  operation_description: string;
  field_changes: FieldChange[];
  requirement_retention_status?: string;
  new_requirement_info?: {
    description: string;
    planStage: string;
    catagory: string;
    catagory2: string;
    subcategory: string;
    priority: string;
    reqSource?: string;
  };
  existing_requirement_info?: {
    description: string;
    planStage: string;
    catagory: string;
    catagory2: string;
    subcategory: string;
    priority: string;
    reqSource?: string;
  };
}

interface OperationStats {
  total_changes: number;
  add_count: number;
  update_count: number;
  delete_count: number;
}

const OP_COLOR: Record<string, string> = {
  '新增': 'green',
  '修改': 'blue',
  '删除': 'red',
};

/** 展开行：显示字段变更详情、新增/删除需求信息、清空BSW承接 */
const ExpandedRow: React.FC<{
  detail: ChangeDetail;
  clearBswSet: Set<string>;
  onToggleClearBsw: (reqId: string, checked: boolean) => void;
}> = ({ detail, clearBswSet, onToggleClearBsw }) => {
  const fieldColumns = [
    { title: '字段', dataIndex: 'field_name', width: 120 },
    {
      title: '数据库原值',
      dataIndex: 'db_value',
      render: (v: string) => (
        <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 280, display: 'block' }}>
          {v || '(空)'}
        </Text>
      ),
    },
    {
      title: '飞书新值',
      dataIndex: 'feishu_value',
      render: (v: string) => (
        <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 280, display: 'block' }}>
          {v || '(空)'}
        </Text>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 16px', background: '#fafafa' }}>
      {/* 需求保留状态 */}
      {detail.requirement_retention_status !== undefined && (
        <div style={{ marginBottom: 8, padding: '6px 10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4 }}>
          <Text strong style={{ fontSize: 12, color: '#d46b08' }}>需求保留状态：</Text>
          <Tag color={detail.requirement_retention_status === '保留' ? 'green' : 'red'} style={{ marginLeft: 8 }}>
            {detail.requirement_retention_status || '(空)'}
          </Tag>
          {detail.requirement_retention_status !== '保留' && (
            <div style={{ fontSize: 11, color: '#d46b08', marginTop: 4 }}>
              ⚠️ 此需求的保留状态不为"保留"，需要特别关注
            </div>
          )}
        </div>
      )}

      {/* 字段变更明细 */}
      {detail.field_changes && detail.field_changes.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>字段变更：</Text>
          <Table
            dataSource={detail.field_changes}
            columns={fieldColumns}
            rowKey="field_key"
            size="small"
            pagination={false}
            bordered
          />
        </div>
      )}

      {/* 新增需求信息 */}
      {detail.new_requirement_info && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>新增需求信息：</Text>
          <div style={{ fontSize: 12, color: '#595959', lineHeight: '20px' }}>
            <div><Text strong>描述：</Text>{detail.new_requirement_info.description}</div>
            <div><Text strong>交付阶段：</Text>{detail.new_requirement_info.planStage}</div>
            <div><Text strong>需求分类：</Text>{detail.new_requirement_info.catagory}</div>
            <div><Text strong>二级分类：</Text>{detail.new_requirement_info.catagory2}</div>
            <div><Text strong>需求分组：</Text>{detail.new_requirement_info.subcategory}</div>
            <div><Text strong>优先级：</Text>{detail.new_requirement_info.priority}</div>
            {detail.new_requirement_info.reqSource && (
              <div><Text strong>备注：</Text>{detail.new_requirement_info.reqSource}</div>
            )}
          </div>
        </div>
      )}

      {/* 现有需求信息（删除时展示） */}
      {detail.existing_requirement_info && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>现有需求信息：</Text>
          <div style={{ fontSize: 12, color: '#595959', lineHeight: '20px' }}>
            <div><Text strong>描述：</Text>{detail.existing_requirement_info.description}</div>
            <div><Text strong>交付阶段：</Text>{detail.existing_requirement_info.planStage}</div>
            <div><Text strong>需求分类：</Text>{detail.existing_requirement_info.catagory}</div>
            <div><Text strong>二级分类：</Text>{detail.existing_requirement_info.catagory2}</div>
            <div><Text strong>需求分组：</Text>{detail.existing_requirement_info.subcategory}</div>
            <div><Text strong>优先级：</Text>{detail.existing_requirement_info.priority}</div>
            {detail.existing_requirement_info.reqSource && (
              <div><Text strong>备注：</Text>{detail.existing_requirement_info.reqSource}</div>
            )}
          </div>
        </div>
      )}

      {/* 清空BSW承接（仅修改类型） */}
      {detail.operation_type === '修改' && (
        <div style={{ padding: '6px 10px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4 }}>
          <Checkbox
            checked={clearBswSet.has(detail.req_id)}
            onChange={(e) => onToggleClearBsw(detail.req_id, e.target.checked)}
          >
            <Text style={{ fontSize: 12 }}>
              清空此需求的"BSW是否承接"字段（让供应商重新填写）
            </Text>
          </Checkbox>
        </div>
      )}
    </div>
  );
};

const FeishuExportModal: React.FC<Props> = ({
  open,
  onClose,
  projectId,
  projectName,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [operationStats, setOperationStats] = useState<OperationStats | null>(null);
  const [changeDetails, setChangeDetails] = useState<ChangeDetail[]>([]);
  const [clearBswSet, setClearBswSet] = useState<Set<string>>(new Set());
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [phaseMsg, setPhaseMsg] = useState('');

  const [parentLink, setParentLink] = useState(
    'https://momenta.feishu.cn/wiki/J9RXwMM1ii6da5kjxeTcMRtunsf?fromScene=spaceOverview'
  );
  const [title, setTitle] = useState(projectName);

  useEffect(() => {
    if (open) {
      setStep('form');
      setOperationStats(null);
      setChangeDetails([]);
      setClearBswSet(new Set());
      setExpandedRowKeys([]);
      setLoading(false);
      setTitle(projectName);
      setParentLink('https://momenta.feishu.cn/wiki/J9RXwMM1ii6da5kjxeTcMRtunsf?fromScene=spaceOverview');
    }
  }, [open, projectName]);

  const buildPayload = (extra: Record<string, unknown>) => ({
    parent_node_feishu_link: parentLink,
    title,
    projectId,
    project: projectName,
    subcatagory: '',
    user_confirmed: false,
    user_cancelled: false,
    ...extra,
  });

  const handleExport = async (confirmed = false) => {
    if (!title.trim()) {
      message.error('标题不能为空');
      return;
    }

    setPhaseMsg('需求导出中...');
    setLoading(true);
    try {
      const res = await exportRequirementsToFeishu(
        projectId,
        buildPayload({
          user_confirmed: confirmed,
          clear_bsw_acceptance_req_ids: confirmed ? Array.from(clearBswSet) : [],
        })
      );
      const data = res.data;

      if (data.code === 200) {
        const changelogLink = data.data?.changelog_link;
        if (changelogLink) {
          message.success(
            <span>
              导出成功！
              <Link href={changelogLink} target="_blank" style={{ marginLeft: 8 }}>
                查看飞书多维表格
              </Link>
            </span>,
            8
          );
        } else {
          message.success(data.message || '数据已成功导出到飞书');
        }
        onSuccess?.();
        onClose();
      } else if (data.code === 202) {
        const details: ChangeDetail[] = data.data?.change_details || [];
        setOperationStats(data.data?.operation_stats || null);
        setChangeDetails(details);
        setExpandedRowKeys([]);
        setStep('confirm');
      } else {
        message.error(data.message || '导出失败');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || '导出失败，请重试');
    } finally {
      setPhaseMsg('');
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await exportRequirementsToFeishu(projectId, buildPayload({ user_cancelled: true }));
    } catch {
      // ignore cancel errors
    } finally {
      setLoading(false);
      setStep('form');
      onClose();
    }
  };

  const toggleClearBsw = (reqId: string, checked: boolean) => {
    setClearBswSet((prev) => {
      const next = new Set(prev);
      checked ? next.add(reqId) : next.delete(reqId);
      return next;
    });
  };

  const mainColumns = [
    {
      title: '操作',
      dataIndex: 'operation_type',
      width: 70,
      render: (v: string) => <Tag color={OP_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '需求ID',
      dataIndex: 'req_id',
      width: 180,
    },
    {
      title: '变更说明',
      dataIndex: 'operation_description',
      ellipsis: true,
    },
    {
      title: '字段变更数',
      dataIndex: 'field_changes',
      width: 90,
      render: (v: FieldChange[]) => (
        <Tag color={v?.length > 0 ? 'blue' : 'default'}>{v?.length || 0} 项</Tag>
      ),
    },
  ];

  const modalTitle = step === 'confirm' ? '确认数据差异' : '导出到飞书';
  const isConfirm = step === 'confirm';

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={() => { if (!loading) { setStep('form'); onClose(); } }}
      width={isConfirm ? 1000 : 520}
      style={isConfirm ? { top: 20 } : undefined}
      styles={isConfirm ? {
        body: {
          height: 'calc(100vh - 110px)',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }
      } : undefined}
      mask={{ closable: !loading }}
      footer={null}
      destroyOnHidden={false}
    >
      <Spin spinning={loading} tip={phaseMsg || undefined}>
        {step === 'form' ? (
          <Form layout="vertical" style={{ marginTop: 8 }}>
            <Form.Item label="飞书父节点链接 (Parent Node Feishu Link)" required>
              <Input
                value={parentLink}
                onChange={(e) => setParentLink(e.target.value)}
                placeholder="请输入父节点飞书链接"
                disabled={loading}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                默认路径为 SAI &gt; SAI团队ODC &gt; SAI-BSW组 &gt; BVP适配 &gt; ReqOutPutTest
              </Text>
            </Form.Item>
            <Form.Item label="标题 (title)" required>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="请输入文档标题"
                disabled={loading}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                文档标题，默认为项目名称
              </Text>
            </Form.Item>
            <Text type="secondary">项目: {projectName}</Text>
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button onClick={onClose} disabled={loading}>取消</Button>
                <Button type="primary" onClick={() => handleExport(false)} loading={loading}>
                  确认导出到飞书
                </Button>
              </Space>
            </div>
          </Form>
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              title="检测到数据差异，请确认是否继续导出"
              style={{ marginBottom: 16 }}
            />
            {operationStats && (
              <Space style={{ marginBottom: 12 }} wrap>
                {operationStats.add_count > 0 && <Tag color="green">新增: {operationStats.add_count}</Tag>}
                {operationStats.update_count > 0 && <Tag color="blue">修改: {operationStats.update_count}</Tag>}
                {operationStats.delete_count > 0 && <Tag color="red">删除: {operationStats.delete_count}</Tag>}
                <Tag>总计: {operationStats.total_changes}</Tag>
              </Space>
            )}
            <Table
              dataSource={changeDetails}
              columns={mainColumns}
              rowKey="req_id"
              size="small"
              pagination={false}
              style={{ flex: 1, marginBottom: 16 }}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                expandedRowRender: (record) => (
                  <ExpandedRow
                    detail={record}
                    clearBswSet={clearBswSet}
                    onToggleClearBsw={toggleClearBsw}
                  />
                ),
                rowExpandable: (record) =>
                  (record.field_changes?.length > 0) ||
                  !!record.new_requirement_info ||
                  !!record.existing_requirement_info ||
                  record.operation_type === '修改',
              }}
            />
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel} disabled={loading}>取消导出</Button>
                <Button type="primary" danger onClick={() => handleExport(true)} loading={loading}>
                  确认执行
                </Button>
              </Space>
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default FeishuExportModal;
